import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import ObliqueIcon from '@/components/icons/ObliqueIcon';

export function FileUpload({ 
    onFileSelect, 
    accept = "*", 
    multiple = false,
    maxSize = 5 * 1024 * 1024, // 5MB default
    className = '' 
}) {
    const [isDragging, setIsDragging] = useState(false);
    const [error, setError] = useState('');
    const inputRef = useRef(null);

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setIsDragging(true);
        } else if (e.type === 'dragleave') {
            setIsDragging(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        setError('');

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            processFiles(Array.from(e.dataTransfer.files));
        }
    };

    const handleChange = (e) => {
        setError('');
        if (e.target.files && e.target.files.length > 0) {
            processFiles(Array.from(e.target.files));
        }
    };

    const processFiles = (files) => {
        const validFiles = files.filter(f => f.size <= maxSize);
        if (validFiles.length !== files.length) {
            setError(`Some files exceeded the maximum size of ${Math.round(maxSize / 1024 / 1024)}MB.`);
        }
        if (validFiles.length > 0) {
            onFileSelect?.(multiple ? validFiles : validFiles[0]);
        }
    };

    return (
        <div className={`w-full ${className}`}>
            <motion.div
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => inputRef.current?.click()}
                className={`
                    w-full flex flex-col items-center justify-center p-8 rounded-2xl border-2 border-dashed
                    cursor-pointer transition-colors
                    ${isDragging 
                        ? 'bg-[var(--accent)]/10 border-[var(--accent)] text-[var(--accent)]' 
                        : 'bg-[var(--bg-surface)] border-[var(--border)] hover:border-[var(--border-strong)] text-[var(--text-secondary)]'}
                `}
            >
                <input 
                    ref={inputRef}
                    type="file" 
                    accept={accept} 
                    multiple={multiple}
                    onChange={handleChange}
                    className="hidden" 
                />
                <motion.div 
                    animate={{ y: isDragging ? -5 : 0 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                    className={`mb-3 ${isDragging ? 'text-[var(--accent)]' : 'text-[var(--text-tertiary)]'}`}
                >
                    <ObliqueIcon name="cloud_upload" size={32} />
                </motion.div>
                <p className="text-sm font-medium mb-1 text-[var(--text-primary)]">
                    <span className="text-[var(--accent)]">Click to upload</span> or drag and drop
                </p>
                <p className="text-xs opacity-70">
                    Max file size: {Math.round(maxSize / 1024 / 1024)}MB
                </p>
            </motion.div>
            
            {error && (
                <p className="text-xs text-red-500 mt-2 font-medium">
                    {error}
                </p>
            )}
        </div>
    );
}
